import React from 'react';

import Tooltip from '~/components/core/Atomic/Tooltip';
import { useCurrencyFormatter } from '~/components/CurrencyFormatterContext';
import { useVatLabel } from '~/components/hooks/useVatLabel';

interface GrossVatTooltipProps {
  /** The gross (incl. VAT/GST) figure the displayed net amount was derived from. */
  grossAmount: number;
  /** The VAT/GST that was netted out of `grossAmount`. */
  vatAmount: number;
  children: React.ReactNode;
}

/**
 * Hover hint that keeps the gross figure - and the VAT/GST netted out of it - reachable on the
 * screens that display net (excl. VAT/GST) amounts.
 *
 * The hint is attached only when there is VAT/GST to disclose (`vatAmount > 0`): with no VAT the net
 * figure equals the gross one, so a "Gross / VAT: 0" hint would be pure noise and the children are
 * returned untouched. `grossAmount` needs no separate guard - every call site derives both values
 * from the same payload, so a finite positive `vatAmount` implies a usable `grossAmount`.
 */
const GrossVatTooltip: React.FC<GrossVatTooltipProps> = ({ grossAmount, vatAmount, children }) => {
  const { currencyFormatter } = useCurrencyFormatter();
  const vatLabel = useVatLabel();

  if (!Number.isFinite(vatAmount) || vatAmount <= 0) {
    return <>{children}</>;
  }

  // The title is passed as a node rather than a string on purpose: a string title is also mirrored onto
  // the child's native `title` attribute, which would replace the accessible name of the amount being
  // wrapped (a table cell would be announced as the hint instead of as the figure it shows).
  const title = (
    <>{`Gross: ${currencyFormatter.format(grossAmount)} / ${vatLabel}: ${currencyFormatter.format(vatAmount)}`}</>
  );

  // MUI clones its child and attaches a ref to it, so the content is wrapped in a host element the
  // ref can land on - the children here are plain text and components that do not forward refs.
  return (
    <Tooltip title={title}>
      <span>{children}</span>
    </Tooltip>
  );
};

export default GrossVatTooltip;
